import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Phone, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const sessionId = params.get("session_id");
  const isPickupPay = params.get("pickup") === "1";

  const [status, setStatus] = useState(isPickupPay ? "pickup" : "polling");
  const [order, setOrder] = useState(null);
  const polled = useRef(0);

  useEffect(() => {
    let cancelled = false;

    async function loadOrder() {
      if (!orderId) return;
      try {
        const { data } = await axios.get(`${API}/orders/${orderId}`);
        if (!cancelled) setOrder(data);
      } catch (err) {
        console.warn("Failed to load order details:", err?.message || err);
      }
    }
    loadOrder();

    async function poll() {
      if (!sessionId || isPickupPay) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          loadOrder();
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        polled.current += 1;
        if (polled.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) {
          polled.current += 1;
          if (polled.current >= 8) setStatus("failed");
          else setTimeout(poll, 2500);
        }
      }
    }
    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId, orderId, isPickupPay]);

  return (
    <div className="max-w-2xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center" data-testid="order-success-page">
      {status === "polling" && (
        <div data-testid="status-polling">
          <Loader2 className="w-12 h-12 text-[#C9853E] mx-auto mb-6 animate-spin" />
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0E0E1A]">
            Confirming your payment…
          </h1>
          <p className="text-[#57534E] mt-3">Hang tight, this usually takes a few seconds.</p>
        </div>
      )}

      {(status === "paid" || status === "pickup") && (
        <div data-testid={status === "paid" ? "status-paid" : "status-pickup"}>
          <CheckCircle2 className="w-16 h-16 text-[#374F3B] mx-auto mb-6" />
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[#0E0E1A]">
            {status === "paid" ? "Payment confirmed!" : "Order placed!"}
          </h1>
          <p className="text-[#57534E] mt-3 text-lg">
            {status === "paid"
              ? "We've started preparing your order."
              : "We've got your order — pay when you arrive."}{" "}
            We'll have it ready in about 25 minutes.
          </p>

          {order && (
            <div className="mt-8 bg-white border border-[#E7E5E4] rounded-3xl p-6 text-left" data-testid="order-summary">
              <div className="flex items-baseline justify-between mb-4">
                <div className="text-xs tracking-[0.2em] uppercase text-[#C9853E] font-bold">
                  Order #{order.id.slice(0, 8)}
                </div>
                <div className="font-display text-xl font-extrabold text-[#0E0E1A]">
                  ${order.subtotal.toFixed(2)}
                </div>
              </div>
              <ul className="text-sm text-[#0E0E1A] divide-y divide-[#E7E5E4]">
                {order.items.map((it, i) => (
                  <li key={i} className="py-2 flex items-baseline justify-between">
                    <span>
                      {it.quantity}× {it.name}
                      {it.variant && (
                        <span className="text-[#57534E]"> — {it.variant}</span>
                      )}
                    </span>
                    <span className="text-[#57534E]">${(it.price * it.quantity).toFixed(2)}</span>
                  </li>
                ))}
              </ul>

              {(order.points_earned > 0 || order.points_redeemed > 0) && (
                <div
                  data-testid="rewards-earned-banner"
                  className="mt-4 -mx-6 -mb-6 px-6 py-4 bg-gradient-to-r from-[#0E0E1A] to-[#1C1B2B] text-white rounded-b-3xl flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#E5B07D]" />
                    <div>
                      {order.points_earned > 0 && (
                        <div className="font-bold">
                          +{order.points_earned} points earned
                        </div>
                      )}
                      {order.points_redeemed > 0 && (
                        <div className="text-xs text-white/70">
                          Used {order.points_redeemed} pts · saved ${order.discount_amount.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-[#E7E5E4] mt-4 pt-4 text-sm text-[#57534E]">
                {order.fulfillment === "delivery" && order.delivery_address ? (
                  <>
                    Delivering to{" "}
                    <strong className="text-[#0E0E1A]">
                      {order.delivery_address.street}
                      {order.delivery_address.unit ? `, ${order.delivery_address.unit}` : ""},{" "}
                      {order.delivery_address.city}, {order.delivery_address.postal_code}
                    </strong>
                    <br />
                    {order.delivery_zone && (
                      <>
                        Zone: <strong className="text-[#0E0E1A]">{order.delivery_zone}</strong>
                        <br />
                      </>
                    )}
                  </>
                ) : (
                  <>
                    Pickup at <strong className="text-[#0E0E1A]">1020 Mayor Magrath Drive S, Lethbridge</strong>
                    <br />
                    Pickup time: <strong className="text-[#0E0E1A]">{order.pickup_time}</strong>
                    <br />
                  </>
                )}
                We'll call <strong className="text-[#0E0E1A]">{order.customer_phone}</strong> if anything's up.
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              data-testid="back-home-btn"
              className="inline-flex items-center gap-2 bg-[#0E0E1A] hover:bg-[#1C1B2B] text-white rounded-full px-6 py-3 font-bold text-sm"
            >
              Back to home
            </Link>
            <a
              href="tel:+14039422565"
              data-testid="success-call-btn"
              className="inline-flex items-center gap-2 bg-white border border-[#0E0E1A] hover:bg-[#0E0E1A] hover:text-white text-[#0E0E1A] rounded-full px-6 py-3 font-bold text-sm transition-all"
            >
              <Phone className="w-3.5 h-3.5" /> Call us
            </a>
          </div>
        </div>
      )}

      {(status === "failed" || status === "timeout") && (
        <div data-testid="status-failed">
          <XCircle className="w-16 h-16 text-[#C9853E] mx-auto mb-6" />
          <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#0E0E1A]">
            We couldn't confirm your payment
          </h1>
          <p className="text-[#57534E] mt-3">
            {status === "timeout"
              ? "It's taking longer than expected. Your card may still be processed — please check your email or call us."
              : "Your payment may have been cancelled or expired."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/order"
              className="inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white rounded-full px-6 py-3 font-bold text-sm"
            >
              Try again
            </Link>
            <a
              href="tel:+14039422565"
              className="inline-flex items-center gap-2 bg-white border border-[#0E0E1A] text-[#0E0E1A] rounded-full px-6 py-3 font-bold text-sm"
            >
              <Phone className="w-3.5 h-3.5" /> Call us
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
